import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import CarouselLeftButton from '../../../public/carousel_left.png';
import CarouselRightButton from '../../../public/carousel_right.png';
import Tutorial0 from '../../../public/tutorial0.png';
import Tutorial1 from '../../../public/tutorial1.png';
import Tutorial2 from '../../../public/tutorial2.png';
import Tutorial3 from '../../../public/tutorial3.png';
import Tutorial4 from '../../../public/tutorial4.png';
import MobileTutorial1 from '../../../public/tutorial_m1.png';
import MobileTutorial2 from '../../../public/tutorial_m2.png';
import MobileTutorial3 from '../../../public/tutorial_m3.png';
import MobileTutorial4 from '../../../public/tutorial_m4.png';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import { useFetchUser } from '../../hooks/useFetchUser';
import useIsMobile from '../../hooks/useIsMobile';
import { modalState } from '../../states/modalState';
import { alertMessage, getOrigin } from '../../utils';
import Picker from '../Picker';
import Checkbox from '../ui/Checkbox';
import RoundButton from '../ui/RoundButton';

type DisplayModeType = 'START' | 'TUTORIAL' | 'AGREEMENT';

interface TutorialContents {
  src: string;
  mobileSrc: string;
  title: string;
  desc: string[];
  mobileDesc?: string[];
  warning?: string;
}

const TUTORIAL_CONTENTS: TutorialContents[] = [
  {
    src: Tutorial1,
    mobileSrc: MobileTutorial1,
    title: '검사지 업로드 화면',
    desc: [
      'AI에게 분석 의뢰를 맡길 의료 관련 문서 이미지를 업로드할 수 있는 메인 화면입니다.',
      '개별 이미지나 폴더를 업로드 할 수 있습니다.',
    ],
    warning:
      '[주의] 서비스 성격에 맞지않는 문서, 글이 포함 돼있지 않은 문서는 상담이 제공되지 않을 수 있습니다. 코인은 예외없이 차감되니 유의해주세요.',
  },
  {
    src: Tutorial2,
    mobileSrc: MobileTutorial2,
    title: '검사지 목록과 분석하기',
    desc: [
      '업로드한 전체 이미지를 날짜와 분석 상태에 따라 필터링할 수 있는 페이지입니다.',
      '특정 이미지를 클릭하면 해당 이미지에 대한 AI 분석 및 상담실 개설이 가능합니다.',
    ],
  },
  {
    src: Tutorial3,
    mobileSrc: MobileTutorial3,
    title: '채팅방',
    mobileDesc: [
      '업로드한 이미지와 AI분석결과 등이 디스플레이 됩니다.',
      '실시간 AI 상담을 진행할 수 있습니다.',
      'AI는 이미지에 대한 분석결과를 학습해두었기 때문에, 이와 관련된 다양한 질문에 유연하게 답변이 가능합니다.',
    ],
    desc: [
      '업로드한 이미지와 AI분석결과 등이 좌측에 디스플레이 됩니다.',
      '우측에서 실시간 AI 상담을 진행할 수 있습니다.',
      'AI는 이미지에 대한 분석결과를 학습해두었기 때문에, 이와 관련된 다양한 질문에 유연하게 답변이 가능합니다.',
    ],
    warning:
      '[주의] 서비스 성격에 맞지않는 문서, 글이 포함 돼있지 않은 문서는 상담이 제공되지 않을 수 있습니다. 코인은 예외없이 차감되니 유의해주세요.',
  },
  {
    src: Tutorial4,
    mobileSrc: MobileTutorial4,
    title: '채팅방 리스트',
    desc: [
      '상담목록 버튼을 누르면, 생성한 채팅방 목록을 조회해 다른 채팅방으로 이동할 수도 있습니다.',
    ],
  },
];

const Tutorial = () => {
  const { logOut } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [displayMode, setDisplayMode] = useState<DisplayModeType>('START');
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentContents, setCurrentContents] = useState<TutorialContents>();
  const [{ isOpen, component }, setModalState] = useRecoilState(modalState);
  const length = TUTORIAL_CONTENTS.length;
  const { requestUserInfo } = useFetchUser();
  const { requestAxios } = useAxios();

  const originUrl = getOrigin();

  const [height, setHeight] = useState(0);

  const [checkedValues, setCheckedValues] = useState({
    selectAll: false,
    checkbox1: false,
    checkbox2: false,
  });

  const containerRef = useCallback((node: HTMLDivElement) => {
    if (!node) {
      return;
    }

    setHeight(node.clientWidth * 0.82);
  }, []);

  useEffect(() => {
    const _currentContents = TUTORIAL_CONTENTS[currentIndex];
    setCurrentContents(_currentContents);
  }, [currentIndex]);

  const Dots = Array.from({ length }, (v, i) => i);

  const goToPrevious = () => {
    if (currentIndex === 0) {
      return;
    }

    setCurrentIndex(currentIndex - 1);
  };

  const goToNext = () => {
    if (currentIndex === length - 1) {
      return;
    }

    setCurrentIndex(currentIndex + 1);
  };

  if (!isOpen || !component) {
    return;
  }

  if (!currentContents) {
    return;
  }

  const onClickStartBtn = () => {
    setDisplayMode('TUTORIAL');
  };

  const onClickSkipBtn = () => {
    setDisplayMode('AGREEMENT');
  };

  const onClickNextBtn = () => {
    if (currentIndex === length - 1) {
      setDisplayMode('AGREEMENT');
      return;
    }

    goToNext();
  };

  const onClickPrevBtn = () => {
    if (currentIndex === 0) {
      setDisplayMode('START');
      return;
    }

    goToPrevious();
  };

  const onClickLogoutBtn = () => {
    logOut();
    alertMessage('로그아웃 되었습니다.');
    navigate('/signin', { replace: true });
  };

  const onClickBackToTutorial = () => {
    // setCurrentIndex(length - 1);
    setDisplayMode('TUTORIAL');
  };

  const onClickConfirmBtn = async () => {
    try {
      const { detail } = await requestAxios<{ detail: string }>(true, {
        url: '/users/my-page/',
        method: 'post',
      });

      alertMessage(detail);
      setModalState({ isOpen: false });
      requestUserInfo();
    } catch (err) {
      console.log(err);
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckedValues((prevValues) => {
      if (name === 'selectAll') {
        return {
          ...prevValues,
          selectAll: checked,
          checkbox1: checked,
          checkbox2: checked,
        };
      } else {
        const newCheckedValues = {
          ...prevValues,
          [name]: checked,
        };

        newCheckedValues.selectAll =
          newCheckedValues.checkbox1 && newCheckedValues.checkbox2;
        return newCheckedValues;
      }
    });
  };

  const StartComponent = () => (
    <>
      <StartWrapper>
        <img
          src={Tutorial0}
          style={{
            width: isMobile ? 'max(260px, calc(100% - 100px))' : '414px',
            maxWidth: '100%',
          }}
        />
        <StartTitle
          style={{
            marginTop: isMobile ? '14px' : '35px',
            fontSize: isMobile ? '20px' : '28px',
            lineHeight: isMobile ? '26px' : '',
          }}
        >
          HEMOCHAT에 오신것을 {isMobile && <br />}환영합니다!
        </StartTitle>
        <RoundButton
          onPress={onClickStartBtn}
          style={{
            width: isMobile ? 'min(calc(100% - 40px), 320px)' : '200px',
            height: '50px',
            marginTop: isMobile ? '30px' : '50px',
            fontSize: '16px',
            fontWeight: 700,
          }}
        >
          시작하기
        </RoundButton>
      </StartWrapper>
    </>
  );

  const TutorialComponent = () => (
    <>
      <CarouselButton
        style={{
          display: isMobile ? 'none' : 'block',
          left: 20,
          backgroundImage: `url(${CarouselLeftButton})`,
          opacity: currentIndex === 0 ? 0 : 1,
          pointerEvents: currentIndex === 0 ? 'none' : 'auto',
        }}
        onClick={goToPrevious}
      />
      <CarouselButton
        style={{
          display: isMobile ? 'none' : 'block',
          right: 20,
          backgroundImage: `url(${CarouselRightButton})`,
          opacity: currentIndex === length - 1 ? 0 : 1,
          pointerEvents: currentIndex === length - 1 ? 'none' : 'auto',
        }}
        onClick={goToNext}
      />
      {isMobile && (
        <MobileSkipButtonWrapper>
          <MobileSkipButton
            onClick={onClickLogoutBtn}
            style={{ textAlign: 'left' }}
          >
            계정전환
          </MobileSkipButton>
          <MobileSkipButton onClick={onClickSkipBtn} style={{}}>
            건너뛰기
          </MobileSkipButton>
        </MobileSkipButtonWrapper>
      )}
      <MainWrapper>
        <CarouselWrapper>
          <TutorialImage
            style={{
              width: '100%',
              height: '320px',
              backgroundImage: `url(${
                currentContents[isMobile ? 'mobileSrc' : 'src']
              })`,
            }}
          />
          <DotsWrapper>
            {Dots.map((dot, idx) => (
              <Dot
                key={idx}
                style={{
                  animation:
                    dot === currentIndex ? 'grow 0.3s forwards' : 'none',
                  backgroundColor:
                    dot === currentIndex ? '#39a2ff' : 'rgba(0, 0, 0, 0.3)',
                }}
              />
            ))}
          </DotsWrapper>
        </CarouselWrapper>
        <ContentsWrapper style={{ height: isMobile ? '220px' : '' }}>
          <Title style={{ fontSize: isMobile ? '18px' : '20px' }}>
            {currentContents.title}
          </Title>
          <DescWrapper style={{ gap: isMobile ? 6 : 8 }}>
            {currentContents[
              isMobile && currentContents?.mobileDesc ? 'mobileDesc' : 'desc'
            ].map((desc, idx) => (
              <Desc
                key={idx}
                style={{
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: isMobile ? '18px' : '',
                }}
              >
                - {desc}
              </Desc>
            ))}
          </DescWrapper>
          {currentContents?.warning && (
            <Warning style={{ fontSize: isMobile ? '13px' : '14px' }}>
              {currentContents.warning}
            </Warning>
          )}
        </ContentsWrapper>
      </MainWrapper>
      {isMobile ? (
        <MobileButtonWrapper>
          <MobileButton
            style={{
              border: '1px solid #1a2e52',
              backgroundColor: 'white',
              color: '#1a2e52',
            }}
            onClick={onClickPrevBtn}
          >
            이전
          </MobileButton>
          <MobileButton onClick={onClickNextBtn}>다음</MobileButton>
        </MobileButtonWrapper>
      ) : (
        <BottomWrapper>
          <span style={{ color: '#21232E', fontSize: '14px' }}>
            계정전환이 필요하신가요?
            <span
              style={{
                marginLeft: '4px',
                color: '#39A2FF',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={onClickLogoutBtn}
            >
              계정전환
            </span>
          </span>
          <ButtonWrapper>
            <Button
              style={{
                width: isMobile ? '78px' : '98px',
                height: isMobile ? '36px' : '44px',
                fontSize: isMobile ? '14px' : '16px',
                border: '1px solid #1a2e52',
                backgroundColor: 'white',
                color: '#1a2e52',
                marginRight: 10,
              }}
              onClick={onClickSkipBtn}
            >
              건너뛰기
            </Button>
            <Button
              style={{
                width: isMobile ? '78px' : '98px',
                height: isMobile ? '36px' : '44px',
                fontSize: isMobile ? '14px' : '16px',
              }}
              onClick={onClickNextBtn}
            >
              다음
            </Button>
          </ButtonWrapper>
        </BottomWrapper>
      )}
    </>
  );

  const AgreementComponent = () => (
    <AgreementWrapper style={{ padding: isMobile ? '5px 20px 0' : '' }}>
      <AgreementTitle
        style={{
          textAlign: isMobile ? 'left' : 'center',
          marginBottom: isMobile ? '20px' : '',
        }}
      >
        서비스 이용약관 동의
      </AgreementTitle>
      <div>
        <AgreementContentWrapper
          style={{
            border: checkedValues.selectAll ? '1px solid #1d90f5' : '',
            backgroundColor: checkedValues.selectAll ? '#e2f2ff' : '',
            padding: isMobile ? '15px 20px' : '20px',
          }}
        >
          <AgreementContent>
            <Checkbox
              id="selectAll"
              name="selectAll"
              checked={checkedValues.selectAll}
              onChange={handleCheckboxChange}
              style={{
                width: '19px',
                height: '19px',
                borderRadius: '50%',
                backgroundColor: '#e6e6e6',
                border: 'none',
              }}
            ></Checkbox>
            <AgreementLabel
              style={{
                fontSize: '16px',
                fontWeight: 700,
                border: 'none',
              }}
              htmlFor="selectAll"
            >
              전체 동의
            </AgreementLabel>
          </AgreementContent>
        </AgreementContentWrapper>
        <AgreementContentWrapper
          style={{
            border: checkedValues.selectAll ? '1px solid #1d90f5' : '',
            backgroundColor: checkedValues.selectAll ? '#e2f2ff' : '',
            marginTop: 6,
          }}
        >
          <AgreementContent>
            <Checkbox
              id="checkbox1"
              name="checkbox1"
              checked={checkedValues.checkbox1}
              onChange={handleCheckboxChange}
              style={{
                width: '19px',
                height: '19px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#e6e6e6',
              }}
            ></Checkbox>
            <AgreementLabel htmlFor="checkbox1">
              (필수) 개인정보 수집 및 이용동의
            </AgreementLabel>

            <a
              target="_blank"
              style={{
                position: 'absolute',
                right: -6,
                fontSize: 12,
                fontWeight: 600,
                width: 20,
                textAlign: 'center',
                backgroundColor: 'transparent',
              }}
              href={`${getOrigin()}/policy/personal-info`}
            >
              〉
            </a>
          </AgreementContent>
          <AgreementContent>
            <Checkbox
              id="checkbox2"
              name="checkbox2"
              checked={checkedValues.checkbox2}
              onChange={handleCheckboxChange}
              style={{
                width: '19px',
                height: '19px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#e6e6e6',
              }}
            ></Checkbox>
            <AgreementLabel htmlFor="checkbox2">
              (필수) 개인정보 제 3자 제공동의
            </AgreementLabel>
            <a
              target="_blank"
              style={{
                position: 'absolute',
                right: -6,
                fontSize: 12,
                fontWeight: 600,
                width: 20,
                textAlign: 'center',
                backgroundColor: 'transparent',
              }}
              href={`${originUrl}/policy/third-info`}
            >
              〉
            </a>
          </AgreementContent>
        </AgreementContentWrapper>
      </div>
      <AgreementButtonWrapper>
        {isMobile ? (
          <AgreementMobileButton
            style={{
              backgroundColor: checkedValues.selectAll ? '#1a2e52' : '#838087',
              color: checkedValues.selectAll ? 'white' : '#cccccc',
            }}
            onClick={onClickConfirmBtn}
            disabled={!checkedValues.selectAll}
          >
            확인
          </AgreementMobileButton>
        ) : (
          <>
            <AgreementButton onClick={onClickBackToTutorial}>
              이전
            </AgreementButton>
            <AgreementButton
              style={{
                backgroundColor: checkedValues.selectAll
                  ? '#1a2e52'
                  : '#ebebeb',
                color: checkedValues.selectAll ? 'white' : 'black',
              }}
              onClick={onClickConfirmBtn}
              disabled={!checkedValues.selectAll}
            >
              확인
            </AgreementButton>
          </>
        )}
      </AgreementButtonWrapper>
    </AgreementWrapper>
  );

  return (
    <>
      <Container
        ref={containerRef}
        style={{
          transition: 'all 0.3s',
          borderRadius: isMobile ? '0' : '20px',
          maxHeight: isMobile ? 'none' : '',
          width: isMobile
            ? '100vw'
            : displayMode === 'AGREEMENT'
            ? 360
            : 'min(calc(100vw - 40px), 1060px)',
          height: isMobile
            ? '100vh'
            : displayMode === 'AGREEMENT'
            ? 331
            : height,
          padding: isMobile
            ? '30px 20px'
            : displayMode === 'AGREEMENT'
            ? '20px 30px'
            : '54px 80px 30px 80px',
        }}
      >
        {displayMode === 'START' && <StartComponent />}
        {(displayMode === 'TUTORIAL' ||
          (isMobile && displayMode === 'AGREEMENT')) && <TutorialComponent />}
        {displayMode === 'AGREEMENT' && !isMobile && <AgreementComponent />}
      </Container>
      {displayMode === 'AGREEMENT' && isMobile && (
        <Picker onClickOutSide={() => setDisplayMode('TUTORIAL')}>
          <AgreementComponent />
        </Picker>
      )}
    </>
  );
};

export default Tutorial;

const Container = styled.div`
  position: relative;
  background-color: white;
  max-height: calc(100vh - 40px);
  aspect-ratio: 53/44;
  display: flex;
  flex-direction: column;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 55%;
  margin-bottom: 18px;
`;

const DotsWrapper = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 18px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 30px;

  @keyframes grow {
    from {
      width: 8px;
    }
    to {
      width: 20px;
    }
  }
`;

const CarouselButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

const TutorialImage = styled.div`
  text-align: center;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
`;

const ContentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  flex-grow: 1;
  flex-shrink: 0;
  min-height: 160px;
`;

const Title = styled.h1`
  font-weight: 700;
`;
const DescWrapper = styled.div`
  margin: 15px 0 10px;
  display: flex;
  flex-direction: column;
`;

const Desc = styled.p`
  font-weight: 400;
  color: #666666;
`;

const Warning = styled.p`
  color: #ff3f3f;
  font-weight: 400;
`;

const BottomWrapper = styled.div`
  width: 100%;
  height: 44px;
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  height: 44px;
  min-height: 44px;
`;

const Button = styled.button`
  background-color: #1a2e52;
  color: white;
  font-weight: 400;
`;

const MobileButtonWrapper = styled.div`
  width: 100%;
  height: 46px;
  min-height: 46px;
  margin-top: 10px;
  gap: 12px;
  display: flex;
`;

const MobileButton = styled.button`
  background-color: #1a2e52;
  color: white;
  font-weight: 700;
  font-size: 14px;
  flex: 1;
  border-radius: 8px;
`;

const StartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
const StartTitle = styled.h1`
  font-weight: 700;
  font-size: 28px;
  text-align: center;
`;

const AgreementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-around;
`;

const AgreementTitle = styled.h1`
  font-weight: 700;
  font-size: 18px;
`;

const AgreementButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const AgreementButton = styled.button`
  width: 155px;
  height: 46px;
  background-color: #ebebeb;
  color: black;
  font-size: 14px;
  font-weight: 400;
  border-radius: 6px;
`;

const AgreementMobileButton = styled(AgreementButton)`
  font-weight: 700;
  width: 100%;
  margin-top: 30px;
`;

const AgreementContentWrapper = styled.div`
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-items: center;
  flex-direction: column;
  padding: 20px;
  gap: 15px;
`;

const AgreementContent = styled.div`
  width: 100%;
  height: 100%;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
`;

const AgreementLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  position: relative;
  bottom: 1px;
  cursor: pointer;
`;

const MobileSkipButton = styled.button`
  text-decoration: underline;
  font-size: 14px;
  color: #333333;
  background-color: transparent;
`;

const MobileSkipButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
